// Viewer preferences of the H0 build: camera mode, the performance meter and its test levers.
// They are kept in localStorage as a per-device convenience, and URL parameters override them
// (handy on the iPad and in automated tests).
import type { CameraMode } from '../render/camera-rig';
import type { Resolution } from '../render/stage';

export interface Settings {
  readonly camera: CameraMode;
  readonly perf: boolean;
  readonly stress: boolean;
  readonly animations: boolean;
  readonly shadows: boolean;
  readonly resolution: Resolution;
  /** Deterministic still frame for screenshots: no animation, no notices. Never stored. */
  readonly shot: boolean;
}

export const DEFAULT_SETTINGS: Settings = {
  camera: 'tilted',
  perf: false,
  stress: false,
  animations: true,
  shadows: true,
  resolution: 'alta',
  shot: false,
};

const STORAGE_KEY = 'islora.ajustes.v1';
const RESOLUTIONS: readonly Resolution[] = ['baja', 'media', 'alta'];

const flag = (value: string | null): boolean | undefined => {
  if (value === null) return undefined;
  return !['0', 'false', 'no', 'off'].includes(value.toLowerCase());
};

/** Settings forced by URL parameters: perf, stress, anim, shadows, res, cam and shot. */
export function parseOverrides(search: string): Partial<Settings> {
  const p = new URLSearchParams(search);
  const out: { -readonly [K in keyof Settings]?: Settings[K] } = {};
  const perf = flag(p.get('perf'));
  if (perf !== undefined) out.perf = perf;
  const stress = flag(p.get('stress'));
  if (stress !== undefined) out.stress = stress;
  const anim = flag(p.get('anim'));
  if (anim !== undefined) out.animations = anim;
  const shadows = flag(p.get('shadows'));
  if (shadows !== undefined) out.shadows = shadows;
  const res = p.get('res');
  if (res && (RESOLUTIONS as readonly string[]).includes(res)) out.resolution = res as Resolution;
  const cam = p.get('cam');
  if (cam === 'cenital' || cam === 'top') out.camera = 'top';
  else if (cam === '3d' || cam === 'tilted') out.camera = 'tilted';
  const shot = flag(p.get('shot'));
  if (shot) {
    out.shot = true;
    out.animations = false;
  }
  return out;
}

/** Keeps only well-formed stored values, so an old or edited entry never breaks the start. */
export function sanitizeStored(raw: unknown): Partial<Settings> {
  if (typeof raw !== 'object' || raw === null) return {};
  const r = raw as Record<string, unknown>;
  const out: { -readonly [K in keyof Settings]?: Settings[K] } = {};
  if (r.camera === 'tilted' || r.camera === 'top') out.camera = r.camera;
  for (const key of ['perf', 'stress', 'animations', 'shadows'] as const) {
    const v = r[key];
    if (typeof v === 'boolean') out[key] = v;
  }
  if (typeof r.resolution === 'string' && (RESOLUTIONS as readonly string[]).includes(r.resolution)) out.resolution = r.resolution as Resolution;
  return out;
}

function readStored(storage: Pick<Storage, 'getItem'> | undefined): Partial<Settings> {
  try {
    const text = storage?.getItem(STORAGE_KEY);
    return text ? sanitizeStored(JSON.parse(text)) : {};
  } catch {
    return {};
  }
}

/**
 * Current settings: defaults, then stored values, then URL overrides. Only the stored values and the
 * fields changed in the app are written back, so a URL override never outlives its address.
 */
export class SettingsStore {
  private stored: Partial<Settings>;
  private settings: Settings;

  constructor(
    search: string,
    private readonly storage: Pick<Storage, 'getItem' | 'setItem'> | undefined,
  ) {
    this.stored = readStored(storage);
    this.settings = { ...DEFAULT_SETTINGS, ...this.stored, ...parseOverrides(search) };
  }

  get current(): Settings {
    return this.settings;
  }

  update(patch: Partial<Omit<Settings, 'shot'>>): void {
    this.settings = { ...this.settings, ...patch };
    if (this.settings.shot) return;
    this.stored = { ...this.stored, ...patch };
    try {
      this.storage?.setItem(STORAGE_KEY, JSON.stringify(this.stored));
    } catch {
      // Private browsing or blocked storage: the preference simply is not remembered.
    }
  }
}

/** localStorage, or undefined where touching it throws. */
export function safeLocalStorage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}
